import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import BrandHeader from "../components/BrandHeader";
import { createSession } from "../lib/api";
import { useSession } from "../context/SessionContext";
import { DiagnoseQuestions, ImproveQuestions, ImproveQuestionsV3, V3_DEFAULTS } from "../components/IntakeQuestions";
import { useConfig } from "../lib/useConfig";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+()\-\d\s.]{7,}$/;

const PATHWAY_META = {
  diagnose: {
    label: "Diagnose My Landscape",
    sub: "Tell us about the property we'll be looking at.",
    tone: "blue",
  },
  improve: {
    label: "Improve My Landscape",
    sub: "Tell us about the property you want to transform.",
    tone: "green",
  },
};

export default function Intake() {
  const [params] = useSearchParams();
  const pathway = params.get("pathway") === "improve" ? "improve" : "diagnose";
  const meta = PATHWAY_META[pathway];
  const navigate = useNavigate();
  const { setSession } = useSession();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip: "",
    property_type: "Front Yard",
    marketing_opt_in: false,
  });
  const [answers, setAnswers] = useState(
    pathway === "diagnose"
      ? { concern: "", first_noticed: "", trend: "", treatments_tried: "", irrigation: "", recent_changes: "" }
      : { improvement_goals: [], additional_notes: "" },
  );
  const [submitting, setSubmitting] = useState(false);
  const cfg = useConfig();
  const v3 = pathway === "improve" && !!cfg?.phase3_enabled;

  useEffect(() => {
    if (v3) {
      setAnswers((a) => ({ ...V3_DEFAULTS, ...a }));
    }
  }, [v3]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!emailRe.test(form.email)) return "Please enter a valid email address.";
    if (!phoneRe.test(form.phone)) return "Please enter a valid phone number.";
    if (form.address.trim().length < 5) return "Please enter the property address.";
    if (form.city.trim().length < 2) return "Please enter the city.";
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) return "Please enter a valid ZIP code.";
    if (pathway === "diagnose" && answers.concern.trim().length < 5) return "Please describe your concern.";
    if (pathway === "improve" && answers.improvement_goals.length === 0) return "Please choose at least one improvement.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const session = await createSession(form, pathway, answers);
      setSession(session);
      toast.success("Got it. Let's upload a photo.");
      navigate("/upload");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start session.");
    } finally {
      setSubmitting(false);
    }
  };

  const accent =
    meta.tone === "blue"
      ? "focus:ring-sky-500/40 border-sky-200"
      : "focus:ring-emerald-500/40 border-emerald-200";
  const btnClass =
    meta.tone === "blue"
      ? "bg-sky-600 hover:bg-sky-700"
      : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8] gf-leaf-pattern">
      <BrandHeader back title="Customer Intake" subtitle={meta.label} />
      <main className="mx-auto max-w-xl px-5 py-8 gf-fade-in">
        <div
          data-testid="intake-pathway-badge"
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-bold ${
            meta.tone === "blue"
              ? "bg-sky-100 text-sky-700"
              : "bg-emerald-100 text-emerald-700"
          }`}
        >
          {meta.label}
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold text-[#092C15] leading-tight">
          Who are we visiting?
        </h1>
        <p className="mt-1 text-slate-600 text-sm">{meta.sub}</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="name">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-testid="intake-name-input"
                id="name"
                className={`pl-9 h-12 bg-white ${accent}`}
                placeholder="Jane Homeowner"
                value={form.name}
                onChange={update("name")}
                autoComplete="name"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-testid="intake-email-input"
                id="email"
                type="email"
                inputMode="email"
                className={`pl-9 h-12 bg-white ${accent}`}
                placeholder="jane@example.com"
                value={form.email}
                onChange={update("email")}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-testid="intake-phone-input"
                id="phone"
                type="tel"
                inputMode="tel"
                className={`pl-9 h-12 bg-white ${accent}`}
                placeholder="(555) 010-1234"
                value={form.phone}
                onChange={update("phone")}
                autoComplete="tel"
                required
              />
            </div>
          </div>

          <div data-testid="marketing-opt-in-row" className="rounded-xl border border-emerald-100 bg-white p-3 flex items-start gap-3">
            <Checkbox
              data-testid="marketing-opt-in-checkbox"
              id="marketing_opt_in"
              checked={form.marketing_opt_in}
              onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_opt_in: v === true }))}
              className="mt-0.5 border-slate-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
            />
            <div className="text-sm">
              <label htmlFor="marketing_opt_in" className="font-medium text-slate-700 select-none cursor-pointer">Keep me updated!</label>
              <p className="text-slate-500 text-xs mt-0.5">
                I want to receive seasonal property health tips, exclusive landscape enhancement offers, and community project updates from GreenFlow.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address">Property Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                data-testid="intake-address-input"
                id="address"
                className={`pl-9 h-12 bg-white ${accent}`}
                placeholder="123 Garden Ln"
                value={form.address}
                onChange={update("address")}
                autoComplete="street-address"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="space-y-1.5 col-span-3">
              <Label htmlFor="city">City</Label>
              <Input data-testid="intake-city-input" id="city" className={`h-12 bg-white ${accent}`} placeholder="Austin" value={form.city} onChange={update("city")} autoComplete="address-level2" required />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input data-testid="intake-zip-input" id="zip" inputMode="numeric" className={`h-12 bg-white ${accent}`} placeholder="78701" value={form.zip} onChange={update("zip")} autoComplete="postal-code" required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Property Area</Label>
            <Select
              value={form.property_type}
              onValueChange={(v) => setForm((f) => ({ ...f, property_type: v }))}
            >
              <SelectTrigger
                data-testid="intake-property-type-trigger"
                className={`h-12 bg-white ${accent}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Front Yard">Front Yard</SelectItem>
                <SelectItem value="Backyard">Backyard</SelectItem>
                <SelectItem value="Side Lawn">Side Lawn</SelectItem>
                <SelectItem value="Full Property">Full Property</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <div className={`text-[11px] uppercase tracking-widest font-bold ${meta.tone === "blue" ? "text-sky-700" : "text-emerald-700"}`}>
              {pathway === "diagnose" ? "About the problem" : "Your priorities"}
            </div>
            <div className="mt-3" data-testid={`intake-${pathway}-questions`}>
              {pathway === "diagnose" ? (
                <DiagnoseQuestions value={answers} onChange={setAnswers} />
              ) : v3 ? (
                <ImproveQuestionsV3 value={{ ...V3_DEFAULTS, ...answers }} onChange={setAnswers} />
              ) : (
                <ImproveQuestions value={answers} onChange={setAnswers} />
              )}
            </div>
          </div>

          <Button
            data-testid="intake-submit-button"
            type="submit"
            disabled={submitting}
            className={`w-full h-12 text-base font-semibold rounded-xl ${btnClass}`}
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} /> Starting…
              </>
            ) : (
              <>
                Continue to Photos <ArrowRight className="ml-2" size={18} />
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            Your info is stored securely for your GreenFlow specialist. We never
            share it.
          </p>
        </form>
      </main>
    </div>
  );
}
